import { useState, useSyncExternalStore } from 'react';
import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';
/**
 * Хук для создания стейта, который имеет стабильную ссылку на объект состояния.
 * Стабильная ссылка на стейт полезна в тех случаях, когда нужно прокидывать данные глубоко
 * по дереву компонентов, но ре-рендерить нужно только листовые компоненты.
 * @param initialValue Изначальное значение стейта.
 * @returns Стейт для реакт-компонентов.
 * @example
 * ```typescrupt
 * const state = useStableState({ foo: 'bar' });
 *
 * // Использование хука
 * const value = state.useState();
 *
 * // Подписка на изменение отдельной части стейта
 * const fooValue = state.useSelector(state => state.foo);
 *
 * // Использование геттера
 * useEffect(() => {
 *     console.log(state.getState());
 * }, [state]);
 *
 * // Модификация стейта
 * const callback = useCallback(() => {
 *     state.setState({ foo: 'bar' });
 * }, [state]);
 * ```
 */
export function useStableState(initialValue) {
    const [stableState] = useState(() => {
        const initialState = initialValue instanceof Function ? initialValue() : initialValue;
        const subscribers = new Set();
        let value = Object.freeze(initialState);
        function subscriber(onStoreChange) {
            const fn = () => onStoreChange();
            subscribers.add(fn);
            return () => {
                subscribers.delete(fn);
            };
        }
        const stableState = {
            getState: () => value,
            setState: (newValue) => {
                value = Object.freeze(newValue);
                subscribers.forEach(fn => fn());
            },
            useState: () => {
                return useSyncExternalStore(subscriber, () => value);
            },
            useSelector: (selector) => {
                return useSyncExternalStoreWithSelector(subscriber, () => value, () => value, selector);
            },
        };
        return stableState;
    });
    return stableState;
}
